import dagre from "@dagrejs/dagre";
import type { Graph, NodeKind, Position, WfEdge, WfNode } from "./types";

export const NODE_W = 240;
export const NODE_H = 92;
export const LANE_H = 200;
export const GRID = 20;

export const DEFAULT_LANES = [
  { index: 0, label: "Applicant & exceptions", labelAr: "مقدم الطلب والاستثناءات" },
  { index: 1, label: "Main path", labelAr: "المسار الرئيسي" },
  { index: 2, label: "Branches & leadership", labelAr: "الفروع والقيادة" },
];

export const clone = <T,>(v: T): T => structuredClone(v);

let seq = 0;
export const newId = (prefix: string) => `${prefix}-${Date.now().toString(36)}${(seq++).toString(36)}`;

/** Stable ids for every node and edge (the server derives the same ones for older versions). */
export function ensureIds(g: Graph): Graph {
  const used = new Set<string>();
  for (const n of g.nodes) { if (!n.id || used.has(n.id)) n.id = uniqueId(`n-${n.key}`, used); used.add(n.id); }
  for (const e of g.edges) { if (!e.id || used.has(e.id)) e.id = uniqueId(`e-${e.from}-${e.trigger}`, used); used.add(e.id); }
  return g;
}

function uniqueId(candidate: string, used: Set<string>) {
  let id = candidate;
  for (let i = 2; used.has(id); i++) id = `${candidate}-${i}`;
  return id;
}

export function keyFrom(label: string) {
  const words = label.split(/[^A-Za-z0-9]+/).filter(Boolean).slice(0, 5);
  const key = words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join("");
  return key.length === 0 ? "Step" : /^\d/.test(key) ? `S${key}` : key;
}

export function uniqueKey(g: Graph, key: string) {
  let candidate = key;
  for (let i = 2; g.nodes.some((n) => n.key === candidate); i++) candidate = `${key}${i}`;
  return candidate;
}

export function uniqueTrigger(g: Graph, from: string, trigger: string) {
  let candidate = trigger;
  for (let i = 2; g.edges.some((e) => e.from === from && e.trigger === candidate); i++) candidate = `${trigger}${i}`;
  return candidate;
}

const KIND_DEFAULTS: Record<NodeKind, { label: string; labelAr: string; status: string }> = {
  Start: { label: "Start", labelAr: "بداية", status: "Submitted" },
  Task: { label: "New step", labelAr: "خطوة جديدة", status: "UnderReview" },
  Decision: { label: "Decision", labelAr: "قرار", status: "Decision" },
  Wait: { label: "Waiting", labelAr: "انتظار", status: "AwaitingApplicant" },
  End: { label: "Closed", labelAr: "مغلق", status: "Closed" },
  Automation: { label: "Automated step", labelAr: "خطوة آلية", status: "UnderReview" },
  Agent: { label: "Agent drafts", labelAr: "مسودة الوكيل", status: "UnderReview" },
  SubProcess: { label: "Sub-process", labelAr: "إجراء فرعي", status: "UnderReview" },
};

export function newNode(g: Graph, kind: NodeKind, position: Position, label?: string): WfNode {
  const d = KIND_DEFAULTS[kind];
  const text = label ?? d.label;
  const key = uniqueKey(g, keyFrom(text));
  return {
    id: newId("n"), key, label: text, labelAr: label ? "" : d.labelAr, kind, status: d.status, actors: [], forms: [], clocks: [], tasks: [], agentHooks: [],
    visibleToApplicant: kind !== "Automation", lane: Math.max(0, Math.min(2, Math.round(position.y / LANE_H))), order: g.nodes.length, position: snap(position),
    triggers: kind === "Start" ? [{ kind: "manual", label: "Opened by staff", enabled: true }] : null,
  };
}

export function newEdge(g: Graph, from: WfNode, to: WfNode, patch?: Partial<WfEdge>): WfEdge {
  const trigger = uniqueTrigger(g, from.key, patch?.trigger ?? defaultTrigger(from, to));
  return {
    id: newId("e"), from: from.key, to: to.key, trigger, label: patch?.label ?? humanize(trigger), labelAr: patch?.labelAr ?? null,
    allowedRoles: from.kind === "Automation" ? [] : [...from.actors], automatic: from.kind === "Automation", completesClocks: [], pausesClocks: [], resumesClocks: [],
    outcome: from.kind === "Agent" ? (g.edges.some((e) => e.from === from.key && e.outcome === "approved") ? "rejected" : "approved") : from.kind === "SubProcess" ? "completed" : null,
    ...patch,
  };
}

function defaultTrigger(from: WfNode, to: WfNode) {
  if (from.kind === "Automation") return "Continue";
  if (from.kind === "Agent") return "DraftApproved";
  if (to.kind === "End") return "Close";
  return `To${to.key}`;
}

export const humanize = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^To /, "To ");

export function snap(p: Position): Position {
  return { x: Math.round(p.x / GRID) * GRID, y: Math.round(p.y / GRID) * GRID };
}

/** Auto-layout with dagre. <c>lanes</c> keeps each state in its swimlane band and orders columns by rank. */
export function autoLayout(g: Graph, mode: "LR" | "TB" | "lanes"): Graph {
  const d = new dagre.graphlib.Graph();
  d.setGraph({ rankdir: mode === "TB" ? "TB" : "LR", nodesep: 50, ranksep: 90, marginx: 20, marginy: 20 });
  d.setDefaultEdgeLabel(() => ({}));
  for (const n of g.nodes) d.setNode(n.key, { width: NODE_W, height: NODE_H });
  for (const e of g.edges) if (e.from !== e.to) d.setEdge(e.from, e.to);
  dagre.layout(d);
  const next = clone(g);
  if (mode === "lanes") {
    const used = new Map<string, number>();
    for (const n of next.nodes) {
      const p = d.node(n.key);
      const col = Math.round((p?.x ?? 0) / (NODE_W + 90));
      const slot = `${col}:${n.lane}`;
      const bump = used.get(slot) ?? 0;
      used.set(slot, bump + 1);
      n.position = snap({ x: col * (NODE_W + 70), y: n.lane * LANE_H + 40 + bump * (NODE_H + 16) });
    }
  } else {
    for (const n of next.nodes) {
      const p = d.node(n.key);
      if (p) n.position = snap({ x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 });
    }
  }
  return next;
}

/** Places states that have no saved position: all of them by auto-layout, a few next to their neighbours. */
export function ensurePositions(g: Graph): Graph {
  const missing = g.nodes.filter((n) => !n.position);
  if (missing.length === 0) return g;
  if (missing.length === g.nodes.length || missing.length > 3) {
    const laid = autoLayout(g, g.nodes.some((n) => n.lane !== 1) ? "lanes" : "LR");
    for (const n of g.nodes) if (!n.position) n.position = laid.nodes.find((x) => x.key === n.key)?.position ?? { x: 0, y: 0 };
    return g;
  }
  for (const n of missing) {
    const neighbour = g.edges.find((e) => e.to === n.key || e.from === n.key);
    const other = neighbour ? g.nodes.find((x) => x.key === (neighbour.to === n.key ? neighbour.from : neighbour.to) && x.position) : null;
    n.position = other?.position ? snap({ x: other.position.x + NODE_W + 60, y: other.position.y + 60 }) : { x: 0, y: (g.nodes.length + 1) * 40 };
  }
  return g;
}

export const laneOf = (y: number) => Math.max(0, Math.min(5, Math.floor((y + NODE_H / 2) / LANE_H)));

/** Rename a state key everywhere it is referenced. */
export function renameKey(g: Graph, from: string, to: string) {
  for (const n of g.nodes) if (n.key === from) n.key = to;
  for (const e of g.edges) { if (e.from === from) e.from = to; if (e.to === from) e.to = to; }
  if (g.initialState === from) g.initialState = to;
}

export function removeNodes(g: Graph, keys: Set<string>) {
  g.nodes = g.nodes.filter((n) => !keys.has(n.key));
  g.edges = g.edges.filter((e) => !keys.has(e.from) && !keys.has(e.to));
}

/** Insert a new state on a transition: from → new → to, keeping the transition's trigger on the first leg. */
export function insertOnEdge(g: Graph, edgeId: string, node: WfNode) {
  const edge = g.edges.find((e) => e.id === edgeId);
  if (!edge) return;
  const to = g.nodes.find((n) => n.key === edge.to);
  g.nodes.push(node);
  const originalTo = edge.to;
  edge.to = node.key;
  if (to) g.edges.push(newEdge(g, node, { ...to, key: originalTo }));
}

export function nodeByKey(g: Graph, key: string) { return g.nodes.find((n) => n.key === key); }

export const isEmptyGraph = (g: Graph) => g.nodes.length === 0;

/** Short commit id for people: the first characters of the content hash, as git shows them. */
export const shortHash = (c?: { hash: string } | null) => (c ? c.hash.slice(0, 7) : "…");
